import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// The typeface is Plus Jakarta Sans, loaded from Google Fonts in index.html — the handoff's
// single external dependency. No font package is bundled.
import './index.css'
import { App } from './App'
import { NotConfiguredError, NotImplementedError, NotSignedInError } from './lib/data/remote'

/**
 * A refusal is not a network blip. NotConfiguredError, NotSignedInError and NotImplementedError
 * are all thrown synchronously, before any fetch happens, and they say the same thing no matter
 * how many times they are asked again in the next few seconds — an unconfigured build stays
 * unconfigured, a signed-out session stays signed out, and a stub arrives with the version it
 * names, not sooner. Retrying one of those three only inserts TanStack Query's exponential
 * backoff (about a second on the first retry) in front of a refusal card that was always going
 * to say the same sentence, on every single visit, because an error result is never treated as
 * settled data the way a success is.
 *
 * A genuine network error (a fetch rejection, a Worker 5xx) gets a couple of retries instead —
 * enough to ride out a blip without turning a real outage into a multi-second skeleton either.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (
    error instanceof NotConfiguredError ||
    error instanceof NotSignedInError ||
    error instanceof NotImplementedError
  ) {
    return false
  }
  return failureCount < 2
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A personal system's data is not contended: nothing changes behind your back except a
      // cron. Refetching on every window focus just burns battery on the phone.
      refetchOnWindowFocus: false,
      // Revisiting a route seconds after leaving it should render instantly from cache, not
      // flash a skeleton while an unnecessary refetch runs. staleTime keeps cached data fresh
      // enough to skip the refetch-on-mount; gcTime keeps it around well past that so a quick
      // back-and-forth between tabs never falls back to an empty cache.
      staleTime: 60_000,
      gcTime: 30 * 60_000,
      retry: shouldRetry,
    },
  },
})

const root = document.getElementById('root')
if (!root) throw new Error('#root is missing from index.html')

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* Opt into the v7 behaviours now rather than discovering them at the upgrade. Also keeps
          the console clean, which matters once the gate starts asserting on console output. */}
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
